import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowLeft,
  CalendarPlus,
  Ellipsis,
  KeyRound,
  Mail,
  Plus,
  ShieldOff,
  Ticket,
  Unplug,
  UserX,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { ROUTES } from '@/constants';
import { EmptyState, ErrorState, LoadingScreen } from '@/components/common';
import {
  useCustomer,
  useEmailPortalPassword,
  useEmailSeatCredentials,
  useReleaseSeat,
  useResetPortalUserPassword,
  useSetPortalUserStatus,
} from '@/services/queries';
import { ApiError } from '@/services/api/client';
import { useAppSelector } from '@/store/hooks';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { cn } from '@/lib/utils';
import type { CustomerLicense, CustomerPortalUser, CustomerSeat } from '@/types';
import { roleLabel } from './credentials';
import { IssueLicenseDialog } from './IssueLicenseDialog';
import { AddSeatDialog } from './AddSeatDialog';
import { ExtendLicenseDialog } from './ExtendLicenseDialog';
import { RevokeSeatDialog, type SeatDialogIntent } from './RevokeSeatDialog';
import { SeatDrawer, type SeatDrawerTarget } from './SeatDrawer';

const MODE_LABEL: Record<string, string> = {
  single_seat: 'Single Seat',
  lab_seat: 'Lab Seat',
};

const STATE_LABEL: Record<string, string> = {
  active: 'Active',
  expiring_soon: 'Expiring',
  expired: 'Expired',
  not_activated: 'Not activated',
};

const STATE_STATUS: Record<string, string> = {
  active: 'active',
  expiring_soon: 'expiring',
  expired: 'expired',
  not_activated: 'unassigned',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** "30 Oct 2026, 14:05" - a heartbeat is worth the time, not just the day. */
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

type MachineState = 'verified' | 'pending' | 'released' | 'revoked';

function machineState(seat: CustomerSeat): MachineState {
  if (seat.status === 'revoked' || seat.status === 'blocked') return 'revoked';
  if (seat.machineFingerprintHash !== null) return 'verified';

  /*
   * No machine bound, and the seat is still sold. Whether that is "not yet" or
   * "not any more" is the difference between never activated and released.
   *
   * Not `activatedAt` any more - the term now starts the moment a seat is
   * bought, so every seat has that set from the start, activated or not.
   * `everActivated` is the real signal: it comes from the seat's device
   * history, which release marks revoked rather than deleting, so it still
   * reads true afterwards.
   */
  return seat.everActivated ? 'released' : 'pending';
}

/**
 * The Seat column, in the three words that actually matter: pending, active,
 * expired.
 *
 * Not a re-label of `seat.status` - that column is written lazily, only when
 * the desktop's own verify check notices the term has run out (see
 * `licensing.repository.ts`), so a seat can sit at `active` for a day or more
 * after `expiresAt` has already passed on a machine nobody has opened. Reading
 * the date here instead means the admin portal says "expired" the moment it
 * is true, not whenever the customer's computer happens to ask.
 *
 * `revoked` and `blocked` are passed through unchanged: those are decisions an
 * administrator or the hardware policy made, not something the term's dates
 * could tell you, and folding them into "expired" would hide that this seat is
 * not merely lapsed but withdrawn.
 */
function seatState(seat: CustomerSeat): string {
  if (seat.status === 'revoked' || seat.status === 'blocked') return seat.status;
  if (seat.expiresAt !== null && new Date(seat.expiresAt).getTime() <= Date.now()) {
    return 'expired';
  }
  // Not `activatedAt` any more - every seat has that set from the moment it
  // is bought, so it can no longer tell "in use" apart from "never touched".
  return seat.everActivated ? 'active' : 'pending';
}

function machineHint(state: MachineState): string {
  switch (state) {
    case 'verified':
      return 'Activated on a computer, which is checking in.';
    case 'pending':
      return 'A licence key has been issued. Nobody has activated it on a computer yet.';
    case 'released':
      return 'The computer this was activated on has been released. Activating again binds a new one.';
    case 'revoked':
      return 'This seat has been withdrawn, so no computer can run on it.';
  }
}

/**
 * The licence's term, as one cell.
 *
 * Validity lives per seat rather than on the licence, because it is counted
 * from the moment that seat is bought - so a seat `addSeat` puts on later can
 * genuinely differ from the licence's original ones. In practice most seats
 * on a licence are issued together and match, which is why this collapses to
 * a single figure and only shows a range when there genuinely is one.
 */
function validityLabel(licence: CustomerLicense): string {
  const days = [...new Set(licence.seats.map((seat) => seat.validityDays))].sort((a, b) => a - b);
  if (days.length === 0) return '—';
  if (days.length === 1) return `${String(days[0])} days`;
  return `${String(days[0])}–${String(days[days.length - 1])} days`;
}

/**
 * Anything inside a row that does its own thing on click. A row click landing
 * on one of these is that control's click, not a request for the drawer.
 */
const ROW_CLICK_EXEMPT =
  'button, a, input, select, textarea, label, [role="button"], [role="menuitem"]';

function isExemptTarget(target: EventTarget): boolean {
  return target instanceof Element && target.closest(ROW_CLICK_EXEMPT) !== null;
}

function licenceOwner(
  licence: CustomerLicense,
  bySeatId: Map<string, CustomerPortalUser>,
): CustomerPortalUser | undefined {
  const people = licence.seats
    .map((seat) => bySeatId.get(seat.id))
    .filter((user): user is CustomerPortalUser => user !== undefined);

  return people.find((user) => user.role === 'owner') ?? people[0];
}

/**
 * Roles allowed to disable a login or reset a password.
 *
 * Mirrors the server, which is the real gate. Hiding a button somebody cannot
 * use is a courtesy - it stops them discovering the limit from a 403 - and never
 * a substitute for the check behind it.
 */
const SUPPORT_ROLES = ['support', 'superadmin'];

/** Who may issue a licence - mirrors the server's PROVISIONERS, a wider set
 *  than SUPPORT_ROLES above: sales closes the deal that needs one. */
const PROVISIONER_ROLES = ['sales', 'support', 'superadmin'];

export function CustomerDetail({ organizationId }: { organizationId: string }) {
  const query = useCustomer(organizationId);
  const role = useAppSelector((state) => state.auth.user?.role);
  const canManage = SUPPORT_ROLES.includes(role ?? '');
  const canProvision = PROVISIONER_ROLES.includes(role ?? '');

  const [resetTarget, setResetTarget] = useState<CustomerPortalUser | null>(null);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [issuingLicense, setIssuingLicense] = useState(false);
  /** Which licence a spare seat is being claimed on, if any. */
  const [addingTo, setAddingTo] = useState<CustomerLicense | null>(null);
  /** Which licence's term is being pushed out, if any. */
  const [extendingLicence, setExtendingLicence] = useState<CustomerLicense | null>(null);
  /**
   * The seat whose key is being taken away or replaced.
   *
   * Holds the seat itself rather than its id: `seat_and_user` deletes the row,
   * and a dialog still reading it out of the refetched customer would blank
   * mid-close.
   */
  const [seatAction, setSeatAction] = useState<{
    seat: CustomerSeat;
    holderName: string | null;
    intent: SeatDialogIntent;
  } | null>(null);
  /**
   * The seat whose drawer is showing. Kept after closing so the drawer still
   * has its content while it slides out, instead of emptying first.
   */
  const [drawerSeatId, setDrawerSeatId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** The last drawer content seen, for a seat a delete has just taken away. */
  const [drawerSnapshot, setDrawerSnapshot] = useState<SeatDrawerTarget | null>(null);
  /** The seat whose machine release is waiting on a confirmation. */
  const [releaseTarget, setReleaseTarget] = useState<{ seatId: string; email: string } | null>(
    null,
  );
  /** The login whose disable/enable is waiting on a confirmation. */
  const [statusTarget, setStatusTarget] = useState<CustomerPortalUser | null>(null);
  /** Licence ids whose people are showing. Several can be open at once. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const setStatus = useSetPortalUserStatus(organizationId);
  const resetPassword = useResetPortalUserPassword();
  const emailPassword = useEmailPortalPassword();
  const releaseSeat = useReleaseSeat(organizationId);
  const resendSeat = useEmailSeatCredentials(organizationId);

  if (query.isPending) return <LoadingScreen label="Loading customer" />;

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <ErrorState
        code={notFound ? '404' : undefined}
        title={notFound ? 'No such customer' : 'Could not load this customer'}
        description={notFound ? 'It may have been removed.' : query.error.message}
        action={
          <Button asChild variant="outline">
            <Link to={ROUTES.customers}>Back to customers</Link>
          </Button>
        }
      />
    );
  }

  const customer = query.data;

  /** seat_id -> the person holding it. `portal_users.seat_id` is the join. */
  const bySeatId = new Map(
    customer.portalUsers
      .filter((user) => user.seatId !== null)
      .map((user) => [user.seatId ?? '', user]),
  );

  /**
   * Built from the live customer on every render rather than stored, so a
   * revoke, reissue or disable done with the drawer's row is reflected in it
   * as soon as the refetch lands.
   */
  const seatTarget = (seatId: string): SeatDrawerTarget | null => {
    for (const licence of customer.licenses) {
      const seat = licence.seats.find((candidate) => candidate.id === seatId);
      if (seat) {
        return {
          seat,
          holder: bySeatId.get(seat.id),
          licenceLabel: MODE_LABEL[licence.mode] ?? licence.mode,
          seatState: seatState(seat),
          machineState: machineState(seat),
        };
      }
    }
    return null;
  };

  const drawerTarget = drawerSeatId ? (seatTarget(drawerSeatId) ?? drawerSnapshot) : null;

  const openSeat = (seatId: string) => {
    setDrawerSeatId(seatId);
    setDrawerSnapshot(seatTarget(seatId));
    setDrawerOpen(true);
  };

  const toggle = (licenceId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(licenceId)) next.delete(licenceId);
      else next.add(licenceId);
      return next;
    });
  };

  const toggleStatus = (user: CustomerPortalUser) => {
    const next = user.status === 'active' ? 'disabled' : 'active';
    setStatus.mutate(
      { portalUserId: user.id, status: next },
      {
        onSuccess: () => {
          toast.success(
            next === 'disabled'
              ? `${user.email} is disabled and signed out everywhere.`
              : `${user.email} can sign in again.`,
          );
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  };

  const release = (seatId: string, email: string) => {
    releaseSeat.mutate(seatId, {
      onSuccess: () => {
        toast.success(`${email} can now be activated on another computer.`);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  // The seat's welcome mail again, password unchanged - for someone who lost it.
  const resend = (seatId: string) => {
    resendSeat.mutate(
      { seatId, resend: true },
      {
        onSuccess: (sent) => {
          toast.success(`Sign-in details sent again to ${sent.toEmail}.`);
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  };

  const confirmReset = () => {
    if (!resetTarget) return;
    resetPassword.mutate(
      { portalUserId: resetTarget.id },
      {
        onSuccess: (result) => {
          // Held in state rather than a toast: it is shown once, and a toast
          // that disappears after four seconds is the wrong container for
          // something that cannot be recovered.
          setNewPassword(result.password ?? null);
        },
        onError: (error) => {
          toast.error(error.message);
          setResetTarget(null);
        },
      },
    );
  };

  const closeReset = () => {
    setResetTarget(null);
    setNewPassword(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to={ROUTES.customers}>
            <ArrowLeft className="size-4" />
            Organizations
          </Link>
        </Button>

        <h1 className="text-2xl font-semibold tracking-tight">{customer.name}</h1>
        <p className="text-sm text-muted-foreground">
          {customer.orgType === 'lab' ? 'Dental lab' : 'Individual dentist'} ·{' '}
          {customer.primaryEmail}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Licences
            <span className="ml-2 font-normal text-muted-foreground">
              {customer.licenses.length}
            </span>
          </CardTitle>
          {canProvision && (
            <CardAction>
              <Button
                size="sm"
                onClick={() => {
                  setIssuingLicense(true);
                }}
              >
                <Plus className="size-4" />
                New licence
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          {customer.licenses.length === 0 ? (
            <EmptyState
              icon={Ticket}
              title="No licence yet"
              description="Issue a licence to get this customer started. It's issued separately, from the organization itself, once the deal is ready."
              actions={
                canProvision && (
                  <Button
                    onClick={() => {
                      setIssuingLicense(true);
                    }}
                  >
                    <Plus className="size-4" />
                    New licence
                  </Button>
                )
              }
            />
          ) : (
            /* table-fixed + explicit column widths: with auto layout, the
               three text columns split whatever width the narrow ones
               (Seats, Validity, ...) don't need, so one or two rows of short
               text leave uneven gaps between columns. Fixed widths make
               every row line up the same regardless of how many there are. */
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[10%]">Seat type</TableHead>
                  <TableHead className="w-[17%]">Owner name</TableHead>
                  <TableHead className="w-[22%]">Owner email</TableHead>
                  <TableHead className="w-[13%]">Bought date</TableHead>
                  <TableHead className="w-[9%]">Seats</TableHead>
                  <TableHead className="w-[13%]">Validity</TableHead>
                  {canManage && <TableHead className="w-px text-center">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {customer.licenses.map((licence) => {
                  const open = expanded.has(licence.id);
                  const owner = licenceOwner(licence, bySeatId);

                  return (
                    <Fragment key={licence.id}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() => {
                          toggle(licence.id);
                        }}
                        aria-expanded={open}
                      >
                        <TableCell className="font-medium">
                          {MODE_LABEL[licence.mode] ?? licence.mode}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {owner?.fullName ?? '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {owner?.email ?? '—'}
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {formatDate(licence.issuedAt)}
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {/* Named over bought: the gap is what is still free
                              to claim, which is the number worth showing. */}
                          {licence.seats.length} / {licence.seatCount}
                        </TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {validityLabel(licence)}
                        </TableCell>
                        {canManage && (
                          <TableCell className="text-center" truncate={false}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(event) => {
                                // The row toggles expand/collapse on click -
                                // without this the button would also open or
                                // close the seats underneath it.
                                event.stopPropagation();
                                setExtendingLicence(licence);
                              }}
                            >
                              <CalendarPlus className="size-4" />
                              Extend
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>

                      {open && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell
                            // Exactly the header's column count: one more and
                            // the table grows a phantom column that squeezes
                            // Actions whenever a licence is open.
                            colSpan={canManage ? 7 : 6}
                            className="bg-muted/20 p-0 whitespace-normal"
                            truncate={false}
                          >
                            <LicenceDetail
                              licence={licence}
                              bySeatId={bySeatId}
                              canManage={canManage}
                              canProvision={canProvision}
                              statusPending={setStatus.isPending}
                              releasePending={releaseSeat.isPending}
                              resendPending={resendSeat.isPending}
                              onResend={resend}
                              onRelease={(seatId, email) => {
                                setReleaseTarget({ seatId, email });
                              }}
                              onAddPerson={() => {
                                setAddingTo(licence);
                              }}
                              onResetPassword={setResetTarget}
                              onToggleStatus={setStatusTarget}
                              onOpenSeat={openSeat}
                              openSeatId={drawerOpen ? drawerSeatId : null}
                              onSeatAction={setSeatAction}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {canProvision && (
        <IssueLicenseDialog
          organizationId={organizationId}
          organizationName={customer.name}
          // Decides which plans are offered: an individual dentist can only
          // hold a Single Seat licence.
          orgType={customer.orgType}
          open={issuingLicense}
          onOpenChange={setIssuingLicense}
        />
      )}

      {canProvision && addingTo && (
        <AddSeatDialog
          organizationId={organizationId}
          licenseId={addingTo.id}
          licenceLabel={MODE_LABEL[addingTo.mode] ?? addingTo.mode}
          seatsFree={addingTo.seatCount - addingTo.seats.length}
          validityLabel={validityLabel(addingTo)}
          open
          onOpenChange={(next) => {
            if (!next) setAddingTo(null);
          }}
        />
      )}

      {canManage && extendingLicence && (
        <ExtendLicenseDialog
          organizationId={organizationId}
          licence={extendingLicence}
          open
          onOpenChange={(next) => {
            if (!next) setExtendingLicence(null);
          }}
        />
      )}

      {seatAction && (
        <RevokeSeatDialog
          organizationId={organizationId}
          seat={seatAction.seat}
          holderName={seatAction.holderName}
          intent={seatAction.intent}
          open
          onOpenChange={(next) => {
            if (!next) setSeatAction(null);
          }}
        />
      )}

      <SeatDrawer
        organizationId={organizationId}
        target={drawerTarget}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />

      {/*
        Releasing unbinds the computer the seat runs on: the desktop there
        stops working on this key until it is activated again, on that
        machine or another. Not something to do by a slipped click.
      */}
      <Dialog
        open={releaseTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReleaseTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Release this machine?</DialogTitle>
            <DialogDescription>
              The computer {releaseTarget?.email ?? ''} activated on is unbound from this seat.
              NμDent on it stops working with this licence key until it is activated again - on the
              same computer or a new one. The key itself does not change.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReleaseTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={releaseSeat.isPending}
              onClick={() => {
                if (!releaseTarget) return;
                release(releaseTarget.seatId, releaseTarget.email);
                setReleaseTarget(null);
              }}
            >
              Release machine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/*
        Disabling signs the person out of every session at once, and enabling
        lets a login back in - neither is a click to make by accident while
        aiming at the button beside it.
      */}
      <Dialog
        open={statusTarget !== null}
        onOpenChange={(open) => {
          if (!open) setStatusTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {statusTarget?.status === 'active' ? 'Disable this login?' : 'Enable this login?'}
            </DialogTitle>
            <DialogDescription>
              {statusTarget?.status === 'active'
                ? `${statusTarget.email} is signed out everywhere immediately and cannot sign in until enabled again.`
                : `${statusTarget?.email ?? ''} will be able to sign in again.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStatusTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={statusTarget?.status === 'active' ? 'destructive' : 'default'}
              disabled={setStatus.isPending}
              onClick={() => {
                if (!statusTarget) return;
                toggleStatus(statusTarget);
                setStatusTarget(null);
              }}
            >
              {statusTarget?.status === 'active' ? 'Disable' : 'Enable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={resetTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeReset();
        }}
      >
        <DialogContent>
          {newPassword === null ? (
            <>
              <DialogHeader>
                <DialogTitle>Reset this password?</DialogTitle>
                <DialogDescription>
                  A new password is generated for {resetTarget?.email} and shown once. They are
                  signed out everywhere immediately.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={closeReset}>
                  Cancel
                </Button>
                <Button onClick={confirmReset} disabled={resetPassword.isPending}>
                  {resetPassword.isPending ? 'Resetting…' : 'Reset password'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>New password</DialogTitle>
                <DialogDescription>
                  Copy it now — it is not stored in readable form and cannot be shown again.
                </DialogDescription>
              </DialogHeader>

              <p className="rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm break-all">
                {newPassword}
              </p>

              <DialogFooter>
                <Button
                  variant="outline"
                  disabled={emailPassword.isPending || resetTarget === null}
                  onClick={() => {
                    if (!resetTarget) return;
                    emailPassword.mutate(
                      { portalUserId: resetTarget.id, password: newPassword },
                      {
                        onSuccess: (sent) => {
                          toast.success(`Sent to ${sent.toEmail}.`);
                        },
                        onError: (error) => {
                          toast.error(error.message);
                        },
                      },
                    );
                  }}
                >
                  {emailPassword.isPending ? 'Sending…' : 'Email it'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(newPassword).then(
                      () => {
                        toast.success('Copied.');
                      },
                      () => {
                        toast.error('Could not copy. Select the text and copy it manually.');
                      },
                    );
                  }}
                >
                  Copy
                </Button>
                <Button onClick={closeReset}>Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * What is inside a licence row: its state, and the people on its seats.
 *
 * The seat and its portal user are shown as ONE row, because they are one
 * thing from an operator's point of view - `portal_users.seat_id` points at
 * the seat, and the address on the seat is the address the person signs in
 * with. Two tables would make somebody join them by eye.
 */
function LicenceDetail({
  licence,
  bySeatId,
  canManage,
  canProvision,
  statusPending,
  releasePending,
  resendPending,
  onAddPerson,
  onResetPassword,
  onToggleStatus,
  onRelease,
  onResend,
  onSeatAction,
  onOpenSeat,
  openSeatId,
}: {
  licence: CustomerLicense;
  bySeatId: Map<string, CustomerPortalUser>;
  canManage: boolean;
  canProvision: boolean;
  statusPending: boolean;
  releasePending: boolean;
  resendPending: boolean;
  onAddPerson: () => void;
  onResetPassword: (user: CustomerPortalUser) => void;
  onToggleStatus: (user: CustomerPortalUser) => void;
  onRelease: (seatId: string, email: string) => void;
  onResend: (seatId: string) => void;
  onSeatAction: (action: {
    seat: CustomerSeat;
    holderName: string | null;
    intent: SeatDialogIntent;
  }) => void;
  onOpenSeat: (seatId: string) => void;
  /** The seat whose drawer is open, to keep its row highlighted. */
  openSeatId: string | null;
}) {
  const seatsFree = licence.seatCount - licence.seats.length;
  const expired = licence.state === 'expired';
  const soon = licence.state === 'expiring_soon';

  return (
    // Stops a click inside from collapsing the row it lives in.
    <div
      className="space-y-3 px-4 py-4"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
        <StatusPill status={STATE_STATUS[licence.state] ?? licence.state} minWidth={92}>
          {STATE_LABEL[licence.state] ?? licence.state}
        </StatusPill>
        <span className="font-mono">
          {licence.seatsBound} / {licence.seatCount} bound
        </span>
        <span
          className={cn(
            'font-mono',
            expired
              ? 'font-medium text-pill-negative-foreground'
              : soon
                ? 'font-medium text-pill-caution-foreground'
                : undefined,
          )}
        >
          Expires {formatDate(licence.expiresAt)}
        </span>
        <span>Issued {formatDate(licence.issuedAt)}</span>
        {licence.salesDealReference && <span>{licence.salesDealReference}</span>}

        <div className="flex-1" />

        {seatsFree > 0 && (
          <span>
            {seatsFree} seat{seatsFree === 1 ? '' : 's'} free
          </span>
        )}
        {canProvision && seatsFree > 0 && (
          <Button size="sm" variant="outline" onClick={onAddPerson}>
            <Plus className="size-4" />
            Add person
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              {/* Paired columns (name over email, activated over expires)
                  so the whole row fits a laptop screen without scrolling. */}
              <TableHead>Person</TableHead>
              <TableHead className="w-px">Role</TableHead>
              <TableHead>Licence key</TableHead>
              <TableHead className="w-px">Status</TableHead>
              <TableHead className="w-px" title="Activated (top) and expires (bottom)">
                Valid
              </TableHead>
              <TableHead className="w-px">Machine</TableHead>
              {/* Uppercase turns μ into Greek capital Mu, which reads as "M". */}
              <TableHead className="w-px">
                N<span className="normal-case">μ</span>Dent version
              </TableHead>
              {canManage && <TableHead className="w-px text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {licence.seats.map((seat) => {
              const user = bySeatId.get(seat.id);

              return (
                /*
                  The row opens the seat's drawer; the action buttons on it
                  keep their own click (see ROW_CLICK_EXEMPT).
                */
                <TableRow
                  key={seat.id}
                  className="cursor-pointer"
                  tabIndex={0}
                  aria-label={`Show ${seat.email}`}
                  data-state={openSeatId === seat.id ? 'selected' : undefined}
                  onClick={(event) => {
                    if (isExemptTarget(event.target)) return;
                    onOpenSeat(seat.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpenSeat(seat.id);
                    }
                  }}
                >
                  {/* Capped rather than table-fixed: this table sizes its
                      columns to their content (the actions column varies), so
                      the free-text ones get a ceiling of their own and
                      ellipsise past it - the full value is on hover. */}
                  <TableCell truncate={false}>
                    <span
                      className="block max-w-[220px] truncate leading-tight font-medium"
                      title={user?.fullName}
                    >
                      {user?.fullName ?? '—'}
                    </span>
                    <span
                      className="block max-w-[220px] truncate text-[12px] leading-tight text-muted-foreground"
                      title={seat.email}
                    >
                      {seat.email}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {user ? roleLabel(user.role) : '—'}
                  </TableCell>
                  <TableCell className="font-mono">
                    {/* Shown deliberately: the customer reads this off the
                        portal and types it into the desktop, and support has
                        to be able to read it back to them.

                        A revoked seat keeps its key in the database - the
                        column is NOT NULL - but showing it would invite
                        somebody to read a dead key down the phone. Activation
                        refuses a revoked seat whatever key is presented. */}
                    {seat.status === 'revoked' ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className="block max-w-[200px] truncate">{seat.licenseKey}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusPill status={seatState(seat)} minWidth={92} />
                  </TableCell>
                  {/*
                    Null until a device activates the seat - there is nothing
                    to show yet, and a placeholder date would claim otherwise.
                    Usually set together with expires_at and the machine hash,
                    since one activation mints all three - except after a
                    reissue, which deliberately keeps this and expires_at while
                    clearing the hash: the seat's term continues, it just has
                    no machine on it yet.
                  */}
                  <TableCell className="font-mono text-muted-foreground" truncate={false}>
                    <span className="block leading-tight">{formatDate(seat.activatedAt)}</span>
                    <span className="block text-[12px] leading-tight">
                      {formatDate(seat.expiresAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {/* The state of the machine, not its fingerprint. See
                        machineState - the hash is a comparison value and says
                        nothing about whether anybody is up and running. */}
                    {(() => {
                      const state = machineState(seat);
                      return (
                        // Hovering says which of the two "revoked" cases this
                        // is, which the one word on the pill cannot.
                        <span title={machineHint(state)}>
                          <StatusPill status={state} minWidth={76} />
                        </span>
                      );
                    })()}
                  </TableCell>
                  {/* From the newest heartbeat of any machine that has held
                      the seat - which version the desktop is running and when
                      it last checked in. A dash until one ever has. */}
                  <TableCell className="font-mono whitespace-nowrap">
                    {seat.lastHeartbeatAt ? (
                      <span className="block leading-tight">
                        <span className="block text-[12px]">{seat.nudentVersion ?? '—'}</span>
                        <span className="block text-[11px] text-muted-foreground">
                          {formatDateTime(seat.lastHeartbeatAt)}
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right" truncate={false}>
                      {/*
                        One menu rather than a row of labelled buttons: five
                        of them pushed the table past the screen. modal={false}
                        because several items open a dialog, and a modal menu
                        closing underneath one leaves the page unclickable.
                      */}
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Actions for ${seat.email}`}
                          >
                            <Ellipsis />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-52">
                          {/* A revoked seat's key no longer activates, so
                              there is nothing to send again. */}
                          {seat.status !== 'revoked' && (
                            <DropdownMenuItem
                              disabled={resendPending}
                              onSelect={() => {
                                onResend(seat.id);
                              }}
                            >
                              <Mail /> Resend sign-in email
                            </DropdownMenuItem>
                          )}
                          {user && (
                            <DropdownMenuItem
                              onSelect={() => {
                                onResetPassword(user);
                              }}
                            >
                              <KeyRound /> Reset password
                            </DropdownMenuItem>
                          )}
                          {/*
                            Only once a machine holds it. The fingerprint
                            column is UNIQUE across every seat, so while it is
                            set this computer can activate nothing else - and
                            until this existed, nothing could lift that. A
                            replaced workstation took its seat with it
                            permanently.
                          */}
                          {seat.machineFingerprintHash && (
                            <DropdownMenuItem
                              disabled={releasePending}
                              onSelect={() => {
                                onRelease(seat.id, seat.email);
                              }}
                            >
                              <Unplug /> Release machine
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {/*
                            A revoked seat is not a dead end: the row is still
                            here, and so is whoever was on it, which is the
                            whole reason revoking keeps the row rather than
                            deleting it.
                          */}
                          {seat.status === 'revoked' ? (
                            <DropdownMenuItem
                              onSelect={() => {
                                onSeatAction({
                                  seat,
                                  holderName: user?.fullName ?? null,
                                  intent: 'reissue',
                                });
                              }}
                            >
                              <Ticket /> Generate licence key
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => {
                                onSeatAction({
                                  seat,
                                  holderName: user?.fullName ?? null,
                                  intent: 'revoke',
                                });
                              }}
                            >
                              <ShieldOff /> Revoke seat
                            </DropdownMenuItem>
                          )}
                          {user && (
                            <DropdownMenuItem
                              variant={user.status === 'active' ? 'destructive' : 'default'}
                              disabled={statusPending}
                              onSelect={() => {
                                onToggleStatus(user);
                              }}
                            >
                              {user.status === 'active' ? <UserX /> : <UserCheck />}
                              {user.status === 'active' ? 'Disable sign-in' : 'Enable sign-in'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {seatsFree > 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={canManage ? 8 : 7}
                  className="text-center text-muted-foreground"
                >
                  {seatsFree} seat{seatsFree === 1 ? '' : 's'} bought and not yet given to anyone.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
